import React from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Target, TrendingUp, AlertCircle, ChevronRight, Lock, FileText, Zap, Share2 } from 'lucide-react'
import useStore from '../store'

// 分享文案模板配置（分数区间匹配）
const SHARE_TEMPLATES = [
  {
    id: 'soul',
    score: 48,
    title: '直击灵魂版',
    content: '系统冷冰冰提示"与央企基础筛选模型匹配度极低，不建议盲目投递"……连做炮灰的资格都没有，太扎心了，你们来比比惨。',
    tag: '暴击'
  },
  {
    id: 'irony',
    score: 43,
    title: '高级反讽版',
    content: '系统委婉地建议我"重点关注市场化程度较高的混合所有制企业"……说人话就是别来蹭国企热度了，去私企卷吧。',
    tag: '反讽'
  },
  {
    id: 'low',
    score: 53,
    title: '低分版',
    content: "央企求职测评喜提53分，系统建议我'拓宽就业视野，不要死磕体制内'……翻译一下就是让我赶紧去私企打工是吧。纯纯的炮灰实锤了，你们快去测测，给自己找点心理平衡。",
    tag: '扎心'
  },
  {
    id: 'position',
    score: 62,
    title: '精准定位版',
    content: '系统没劝退，只是淡淡留了一句"建议将预期调整为地方城投/非核心岗"……行吧，起码让我知道自己的底线在哪了，你们测测看自己什么段位。',
    tag: '清醒'
  },
  {
    id: 'geo',
    score: 65,
    title: '地缘锁死版',
    content: '系统直接判定我不回老家/生源地就是"纯陪跑"……大城市梦碎了，这测评真是不留情面，你们也去看看自己被地缘卡死没。',
    tag: '扎心'
  },
  {
    id: 'urgent',
    score: 68,
    title: '紧迫版',
    content: '央国企测评68分，系统直接建议我放弃总部管培生，去卷三级子公司的基层岗……虽然难听，但好像确实是这么回事，有没有人一起研究下怎么曲线救国？',
    tag: '限时'
  },
  {
    id: 'data',
    score: 72,
    title: '数据版',
    content: '我参加央国企求职测评，得了72分，竞争比想象中激烈，今年这么难吗？你们也来试试能拿多少分。',
    tag: '热门'
  },
  {
    id: 'major',
    score: 74,
    title: '专业背刺版',
    content: '央国企测评74分，全靠学校撑着。系统说我专业被标了"红牌警告"，直接扣了一大截……难怪之前网申总挂，原来是被专业背刺了，你们快去查查。',
    tag: '破防'
  },
  {
    id: 'hidden',
    score: 75,
    title: '隐性门槛版',
    content: '拿了75分，结果带个括号备注：受年龄/性别隐性限制，部分核心岗被锁。终于知道为什么投简历石沉大海了，你们也去测测有没有被暗中针对。',
    tag: '揭秘'
  },
  {
    id: 'default',
    score: 85,
    title: '标准版',
    content: '硬条件测评拿了85分，以为稳了，结果系统提示跟我一样条件的"神仙"太多了，属于同质化内卷区，这年头好条件也不吃香了，你们来卷卷看。',
    tag: '推荐'
  },
  {
    id: 'benefit',
    score: 88,
    title: '福利版',
    content: '我参加央国企求职测评，得了88分，看来留北京没啥希望了，你们也来试试。',
    tag: '免费'
  }
]

// 根据分数获取最接近的3个模板
function getNearestTemplates(score) {
  // 计算每个模板与当前分数的差值
  const withDiff = SHARE_TEMPLATES.map(t => ({
    ...t,
    diff: Math.abs(t.score - score)
  }))
  
  // 按差值排序，取前3个
  const sorted = withDiff.sort((a, b) => a.diff - b.diff)
  return sorted.slice(0, 3)
}

// SVG Radar Chart Component
const RadarChart = ({ data, score, delay = 0 }) => {
  const width = 280
  const height = 280
  const centerX = width / 2
  const centerY = height / 2
  const radius = 100
  
  const items = Object.entries(data).map(([key, item]) => ({
    key,
    label: item.label,
    score: item.score,
    fullLabel: getFullLabel(key, item.label)
  }))
  
  const angleStep = (2 * Math.PI) / items.length
  const maxScore = 100
  
  const getPoint = (score, index) => {
    const angle = index * angleStep - Math.PI / 2
    const r = (score / maxScore) * radius
    return {
      x: centerX + r * Math.cos(angle),
      y: centerY + r * Math.sin(angle)
    }
  }
  
  const gridLevels = [20, 40, 60, 80, 100]
  
  const dataPoints = items.map((item, i) => getPoint(item.score, i))
  const dataPath = dataPoints.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ') + ' Z'
  
  const getScoreColor = () => {
    if (score >= 75) return '#22c55e'
    if (score >= 50) return '#3b82f6'
    if (score >= 30) return '#eab308'
    return '#ef4444'
  }
  
  const mainColor = getScoreColor()
  
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay, duration: 0.6 }}
      className="flex flex-col items-center"
    >
      <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
        {gridLevels.map((level) => {
          const points = items.map((_, i) => {
            const p = getPoint(level, i)
            return `${p.x},${p.y}`
          }).join(' ')
          return (
            <polygon
              key={level}
              points={points}
              fill="none"
              stroke="#e5e7eb"
              strokeWidth="1"
              strokeDasharray={level === 100 ? "none" : "4,4"}
            />
          )
        })}
        
        {items.map((_, i) => {
          const end = getPoint(100, i)
          return (
            <line
              key={`axis-${i}`}
              x1={centerX}
              y1={centerY}
              x2={end.x}
              y2={end.y}
              stroke="#e5e7eb"
              strokeWidth="1"
            />
          )
        })}
        
        <motion.path
          d={dataPath}
          fill={mainColor}
          fillOpacity="0.2"
          stroke={mainColor}
          strokeWidth="2"
          initial={{ pathLength: 0, opacity: 0 }}
          animate={{ pathLength: 1, opacity: 1 }}
          transition={{ delay: delay + 0.3, duration: 1 }}
        />
        
        {dataPoints.map((p, i) => (
          <motion.circle
            key={`point-${i}`}
            cx={p.x}
            cy={p.y}
            r="5"
            fill={mainColor}
            stroke="white"
            strokeWidth="2"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: delay + 0.5 + i * 0.1, type: 'spring' }}
          />
        ))}
        
        {items.map((item, i) => {
          const angle = i * angleStep - Math.PI / 2
          const labelRadius = radius + 22
          const x = centerX + labelRadius * Math.cos(angle)
          const y = centerY + labelRadius * Math.sin(angle)
          
          return (
            <g key={`label-${i}`}>
              <text
                x={x}
                y={y}
                textAnchor="middle"
                dominantBaseline="middle"
                className="text-xs font-medium fill-gray-600"
              >
                {item.fullLabel}
              </text>
              <text
                x={x}
                y={y + 14}
                textAnchor="middle"
                dominantBaseline="middle"
                className="text-xs font-bold"
                fill={mainColor}
              >
                {item.score}分
              </text>
            </g>
          )
        })}
        
        <circle cx={centerX} cy={centerY} r="28" fill="white" stroke={mainColor} strokeWidth="2" />
        <text
          x={centerX}
          y={centerY - 2}
          textAnchor="middle"
          dominantBaseline="middle"
          className="text-lg font-bold"
          fill={mainColor}
        >
          {score}
        </text>
        <text
          x={centerX}
          y={centerY + 12}
          textAnchor="middle"
          dominantBaseline="middle"
          className="text-xs fill-gray-500"
        >
          总分
        </text>
      </svg>
    </motion.div>
  )
}

function getFullLabel(key, label) {
  const labelMap = {
    school: '院校层次',
    major: '专业匹配',
    age: '年龄优势',
    political: '政治面貌',
    identity: '应届身份',
    gender: '性别均衡'
  }
  return labelMap[key] || label
}

// Share Template Card Component
const ShareTemplateCard = ({ template, index, onClick }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: 0.2 + index * 0.1 }}
    onClick={onClick}
    className="bg-white rounded-xl border-2 border-gray-200 p-4 cursor-pointer hover:border-primary-400 hover:shadow-lg transition-all"
  >
    <div className="flex items-center justify-between mb-2">
      <span className="text-sm font-bold text-gray-800">{template.title}</span>
      <span className="text-xs px-2 py-0.5 rounded-full bg-primary-100 text-primary-700">
        {template.tag}
      </span>
    </div>
    <p className="text-xs text-gray-500 line-clamp-2">{template.content}</p>
    <div className="mt-2 text-xs text-primary-600 font-medium">
      参考分数: {template.score}分
    </div>
  </motion.div>
)

const MatchResultPage = () => {
  const navigate = useNavigate()
  const { matchResult, assessmentData } = useStore()

  if (!matchResult) {
    return (
      <div className="text-center py-20">
        <AlertCircle size={48} className="mx-auto text-gray-400 mb-4" />
        <p className="text-gray-600 mb-4">请先完成硬条件匹配测评</p>
        <button onClick={() => navigate('/match')} className="btn-primary">
          去测评
        </button>
      </div>
    )
  }

  const { score, tier, advice, insight, details, isLowScore } = matchResult

  // 获取最接近的3个分享模板
  const nearestTemplates = getNearestTemplates(score)

  const getScoreColor = () => {
    if (score >= 75) return 'text-green-500'
    if (score >= 50) return 'text-primary-500'
    if (score >= 30) return 'text-yellow-500'
    return 'text-red-500'
  }

  const getScoreBg = () => {
    if (score >= 75) return 'bg-green-50 border-green-200'
    if (score >= 50) return 'bg-primary-50 border-primary-200'
    if (score >= 30) return 'bg-yellow-50 border-yellow-200'
    return 'bg-red-50 border-red-200'
  }

  const getTierLabel = () => {
    const labels = { A: '优秀', B: '良好', C: '一般', D: '需提升' }
    return labels[tier] || '未知'
  }

  const copyShareText = (template) => {
    const text = `${template.content}\n\nhttps://www.grad-job.com\n\n#央国企求职 #应届生求职 #求职测评`
    navigator.clipboard.writeText(text)
    alert('分享文案已复制到剪贴板')
  }

  return (
    <div className="max-w-3xl mx-auto">
      {/* 结果卡片 */}
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
        className={`card p-8 mb-6 border-2 ${getScoreBg()}`}
      >
        <div className="text-center mb-6">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
            className="inline-block"
          >
            <div className={`text-5xl md:text-6xl font-bold ${getScoreColor()} mb-2`}>
              {score}分
            </div>
          </motion.div>
          <div className="text-lg text-gray-600 mb-1">
            央国企求职匹配度
          </div>
          <div className={`inline-block px-4 py-1 rounded-full text-sm font-bold ${getScoreColor()} bg-white`}>
            {getTierLabel()} - {advice}
          </div>
        </div>

        {/* 雷达图 + 维度详情 */}
        <div className="bg-white rounded-xl p-6 mb-6">
          <div className="flex flex-col md:flex-row items-center gap-6">
            <div className="flex-shrink-0">
              <RadarChart data={details} score={score} delay={0.3} />
            </div>
            
            <div className="flex-1 w-full">
              <div className="flex items-center mb-4">
                <TrendingUp className="text-primary-600 mr-2" size={20} />
                <h3 className="font-bold text-gray-900">各维度得分</h3>
              </div>
              
              <div className="space-y-3">
                {Object.entries(details).map(([key, item], index) => (
                  <motion.div
                    key={key}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.5 + index * 0.1 }}
                  >
                    <div className="flex justify-between mb-1">
                      <span className="text-sm text-gray-600">{getFullLabel(key, item.label)}</span>
                      <span className="text-sm font-bold text-gray-900">{item.score}分</span>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-2">
                      <motion.div
                        className={`h-full rounded-full ${
                          item.score >= 80 ? 'bg-green-500' :
                          item.score >= 60 ? 'bg-primary-500' :
                          item.score >= 40 ? 'bg-yellow-500' : 'bg-red-500'
                        }`}
                        initial={{ width: 0 }}
                        animate={{ width: `${item.score}%` }}
                        transition={{ delay: 0.6 + index * 0.1, duration: 0.8 }}
                      />
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* 分享文案推荐 - 只显示3个最接近的 */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="bg-white rounded-xl p-6 mb-6"
        >
          <div className="flex items-center mb-4">
            <Share2 className="text-primary-600 mr-2" size={20} />
            <h3 className="font-bold text-gray-900">分享你的测评结果</h3>
            <span className="ml-2 text-xs text-gray-500">(已为你匹配最接近的文案)</span>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {nearestTemplates.map((template, index) => (
              <ShareTemplateCard
                key={template.id}
                template={template}
                index={index}
                onClick={() => copyShareText(template)}
              />
            ))}
          </div>
          
          <p className="text-xs text-gray-400 mt-3 text-center">
            点击卡片即可复制分享文案
          </p>
        </motion.div>

        {/* 低分用户特殊处理 */}
        {isLowScore ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-white rounded-xl p-6 mb-6"
          >
            <div className="flex items-start space-x-3">
              <AlertCircle className="text-brand-red shrink-0 mt-1" size={24} />
              <div>
                <p className="text-gray-700 mb-4 leading-relaxed">
                  你的初步匹配结果不理想，但这不代表没有机会。
                  很多成功案例表明，通过精准定位和充分准备，
                  条件普通的候选人也能找到合适的央国企岗位。
                </p>
                <p className="text-gray-700 mb-6 leading-relaxed">
                  花19.9元做一个全面测评，看看完整分析能否发现一些进入央国企的可能性。
                </p>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => navigate('/payment?type=precision&amount=19.9')}
                  className="w-full btn-primary py-4 text-lg flex items-center justify-center"
                >
                  <Zap size={20} className="mr-2" />
                  19.9元查看完整可能性分析
                </motion.button>
              </div>
            </div>
          </motion.div>
        ) : (
          <>
            {/* 免费洞察 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white rounded-xl p-6 mb-6"
            >
              <div className="flex items-center mb-3">
                <Target className="text-primary-600 mr-2" size={20} />
                <h3 className="font-bold text-gray-900">专属洞察</h3>
              </div>
              <p className="text-gray-700 leading-relaxed">{insight}</p>
            </motion.div>

            {/* 付费引导 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8 }}
              className="bg-gradient-to-r from-primary-600 to-primary-500 rounded-xl p-6 text-white"
            >
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center shrink-0">
                  <FileText size={24} />
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-lg mb-2">以上为初步硬条件匹配</h3>
                  <p className="text-primary-100 mb-4">
                    想要精准测算你的央国企匹配度？少喝一杯奶茶，
                    大数据来告诉你怎样提升央国企求职竞争力
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => navigate('/payment?type=precision&amount=19.9')}
                      className="bg-white text-primary-600 font-bold px-6 py-3 rounded-lg hover:bg-primary-50 transition-colors flex items-center justify-center"
                    >
                      我要精准匹配
                      <ChevronRight size={18} className="ml-1" />
                    </motion.button>
                  </div>
                  <div className="mt-3 text-xs text-primary-200">
                    包含：AI简历分析 + 6维度雷达图 + 精准提升策略
                  </div>
                </div>
              </div>
            </motion.div>
          </>
        )}

        {/* 算法说明 + 顾问引导 */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.0 }}
          className="bg-gradient-to-r from-gray-800 to-gray-900 rounded-xl p-6 text-white mb-6"
        >
          <div className="flex items-start space-x-4">
            <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center shrink-0">
              <Zap size={20} className="text-yellow-400" />
            </div>
            <div>
              <p className="text-gray-300 text-sm leading-relaxed mb-4">
                本分数由<span className="text-yellow-400 font-bold">100+隐性标签交叉加权</span>得出，非简单赋分。
                想知道大数据的算法底层逻辑和学校/专业的精准差异？
              </p>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate('/packages')}
                className="bg-yellow-500 text-gray-900 font-bold px-6 py-2.5 rounded-lg hover:bg-yellow-400 transition-colors text-sm flex items-center"
              >
                <Target size={16} className="mr-2" />
                找求职顾问，解锁你的专属算法底牌
              </motion.button>
            </div>
          </div>
        </motion.div>

        {/* 免责声明 */}
        <div className="mt-6 text-center text-xs text-gray-400 flex items-center justify-center">
          <Lock size={12} className="mr-1" />
          测评结果仅供参考，不作为录取保证 | 数据已加密存储
        </div>
      </motion.div>
    </div>
  )
}

export default MatchResultPage
